// [2026-10-29] ADDED — INC-2049 (Rujal, 2026-10-29: "in the browser"): the Suggestions list survives a page refresh,
// the Rejoin button and reopening the link on the same computer. Live follow-ups, code cards and "+ Get a question"
// cards live only in redux (the server keeps the starters plan alone), so a page load used to leave just the 4
// starters. This module saves what the list needs into localStorage, per interview, for 24 hours, and merges it back
// after a load:
//   - the cards (field whitelist only: no answer, transcript or resume text);
//   - from copilotUi: which cards were cleared, unread, their order (firstSeen/seq) and the "New" batch;
//   - the Proctoring tab's seen ids (INC-2050), so its count stays cleared after a refresh too;
//   - [2026-11-03] ADDED — from copilotUi: the Bookmarks tab's seen ids (bookmarksSeen, short hashes, no question text),
//     for the same reason. Only saved once the tab was opened, so a snapshot without them is exactly as before.
// Never saved: aiCopilotLastSeq (the request counter restarts on a load; a restored value would drop every new reply),
// undo/toast/flash/open/tab. Pure (no React, no redux): node-tested in __tests__/copilotSessionStore.test.mjs. Every
// storage call is in try/catch: a blocked or full storage only means the list is not kept, never an error.
import { normalizeCopilotQuestion } from './copilotListModel';

export const SESSION_KEY_PREFIX = 'incassist.copilot.session.v1:';
export const SESSION_TTL_MS = 24 * 60 * 60 * 1000;
export const SESSION_MAX_CARDS = 200; // per list; far above a real interview
export const SESSION_MAX_TEXT = 1000; // characters per question
export const SESSION_MAX_BYTES = 200 * 1000; // a bigger snapshot is not saved
const SESSION_VERSION = 1;

const CARD_FIELDS = [
    'id',
    'question',
    'skill',
    'skill_type',
    'difficulty_level',
    'resume_hint',
    'job_skill',
    'intent',
    'prompt_version',
    'slot',
    'answer_key',
    'basis',
];

const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const sessionKey = (interviewId) => `${SESSION_KEY_PREFIX}${interviewId}`;

/** One card with only the fields the list shows (strings capped); null for a card without question text. */
export function pickCard(q) {
    if (!isObj(q) || typeof q.question !== 'string' || !q.question.trim()) return null;
    const out = {};
    CARD_FIELDS.forEach((k) => {
        const v = q[k];
        if (v === undefined || v === null) return;
        if (typeof v === 'string') out[k] = v.slice(0, SESSION_MAX_TEXT);
        else if (typeof v === 'number' || typeof v === 'boolean') out[k] = v;
    });
    return out;
}

const pickCards = (list) => (Array.isArray(list) ? list : []).map(pickCard).filter(Boolean).slice(0, SESSION_MAX_CARDS);

// id → true / id → number maps, keys and values checked
const pickFlags = (map) => {
    const out = {};
    if (isObj(map)) Object.keys(map).forEach((k) => map[k] === true && (out[k] = true));
    return out;
};
const pickRanks = (map) => {
    const out = {};
    if (isObj(map))
        Object.keys(map).forEach((k) => {
            if (Number.isFinite(map[k])) out[k] = map[k];
        });
    return out;
};

/** The part of state.Interview worth keeping. */
export function sessionSnapshot(interview) {
    const s = isObj(interview) ? interview : {};
    const ui = isObj(s.copilotUi) ? s.copilotUi : {};
    return {
        followUps: pickCards(s.aiCopilotQuestions),
        codeQuestions: pickCards(s.copilotCodeQuestions),
        skillQuestions: pickCards(s.copilotSkillQuestions),
        ui: {
            cleared: pickFlags(ui.cleared),
            unread: pickFlags(ui.unread),
            firstSeen: pickRanks(ui.firstSeen),
            seq: Number.isFinite(ui.seq) ? ui.seq : 0,
            latestBatch: pickFlags(ui.latestBatch),
            // [2026-11-03] ADDED — INC-279 IncAssist: the Bookmarks tab's seen ids, only once the tab was opened.
            ...(isObj(ui.bookmarksSeen) ? { bookmarksSeen: pickFlags(ui.bookmarksSeen) } : {}),
        },
        proctoringSeenIds: isObj(s.copilotProctoringSeenIds) ? pickFlags(s.copilotProctoringSeenIds) : null,
    };
}

// [2026-11-03] CHANGED — INC-279 IncAssist: + "the Bookmarks tab was opened" also makes a snapshot worth saving. Was the
// same list without the last line.
const isEmptySnapshot = (snap) =>
    !snap.followUps.length &&
    !snap.codeQuestions.length &&
    !snap.skillQuestions.length &&
    !Object.keys(snap.ui.cleared).length &&
    !snap.proctoringSeenIds &&
    !snap.ui.bookmarksSeen;

const readEntry = (storage, key, now) => {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!isObj(parsed) || parsed.v !== SESSION_VERSION || !Number.isFinite(parsed.savedAt)) return 'bad';
    if (now - parsed.savedAt > SESSION_TTL_MS || parsed.savedAt - now > SESSION_TTL_MS) return 'bad';
    return parsed;
};

/** Removes other interviews' saved lists that expired or cannot be read. Never throws. */
export function pruneSessions(storage, now = Date.now(), keepKey = '') {
    try {
        const keys = [];
        for (let i = 0; i < storage.length; i += 1) {
            const k = storage.key(i);
            if (typeof k === 'string' && k.startsWith(SESSION_KEY_PREFIX) && k !== keepKey) keys.push(k);
        }
        keys.forEach((k) => {
            let entry = 'bad';
            try {
                entry = readEntry(storage, k, now);
            } catch (e) {
                entry = 'bad';
            }
            if (entry === 'bad') storage.removeItem(k);
        });
    } catch (e) {
        // storage unavailable: nothing to prune
    }
}

/** Saves this interview's list. Returns true when written. Never throws. */
export function saveSession(storage, interviewId, interview, now = Date.now()) {
    if (!storage || !interviewId) return false;
    try {
        const snap = sessionSnapshot(interview);
        if (isEmptySnapshot(snap)) return false;
        const raw = JSON.stringify({ v: SESSION_VERSION, savedAt: now, ...snap });
        if (raw.length > SESSION_MAX_BYTES) return false;
        const key = sessionKey(interviewId);
        pruneSessions(storage, now, key);
        storage.setItem(key, raw);
        return true;
    } catch (e) {
        return false;
    }
}

/** This interview's saved list (sessionSnapshot shape), or null (none, expired, unreadable). Never throws. */
export function loadSession(storage, interviewId, now = Date.now()) {
    if (!storage || !interviewId) return null;
    const key = sessionKey(interviewId);
    try {
        const entry = readEntry(storage, key, now);
        if (entry === 'bad') {
            storage.removeItem(key);
            return null;
        }
        if (!entry) return null;
        // Re-pick on the way in: the stored value is only data.
        return sessionSnapshot({
            aiCopilotQuestions: entry.followUps,
            copilotCodeQuestions: entry.codeQuestions,
            copilotSkillQuestions: entry.skillQuestions,
            copilotUi: entry.ui,
            copilotProctoringSeenIds: entry.proctoringSeenIds,
        });
    } catch (e) {
        try {
            storage.removeItem(key);
        } catch (e2) {
            // ignore
        }
        return null;
    }
}

// Cards already in redux come first (they are the newest), then the saved ones; one card per text (follow-ups) or id.
const mergeCards = (current, saved, keyOf) => {
    const seen = new Set();
    const out = [];
    [...(Array.isArray(current) ? current : []), ...(Array.isArray(saved) ? saved : [])].forEach((q) => {
        const k = q && keyOf(q);
        if (!k || seen.has(k)) return;
        seen.add(k);
        out.push(q);
    });
    return out;
};

/**
 * The state.Interview keys after a restore (merge, never wipe). The saved order numbers win; ids registered only since
 * the load are renumbered above the saved ones in their own order, so they stay the newest.
 */
export function mergeSession(interview, saved) {
    const s = isObj(interview) ? interview : {};
    if (!isObj(saved)) return {};
    const ui = isObj(s.copilotUi) ? s.copilotUi : {};
    const savedUi = isObj(saved.ui) ? saved.ui : {};
    const savedRanks = isObj(savedUi.firstSeen) ? savedUi.firstSeen : {};
    const curRanks = isObj(ui.firstSeen) ? ui.firstSeen : {};
    const savedSeq = Number.isFinite(savedUi.seq) ? savedUi.seq : 0;

    const firstSeen = { ...curRanks, ...savedRanks };
    const unread = { ...(isObj(ui.unread) ? ui.unread : {}) };
    Object.keys(savedRanks).forEach((id) => {
        if (savedUi.unread && savedUi.unread[id]) unread[id] = true;
        else delete unread[id];
    });
    const newOnly = Object.keys(curRanks)
        .filter((id) => savedRanks[id] === undefined)
        .sort((a, b) => curRanks[a] - curRanks[b]);
    let seq = Math.max(savedSeq, ...Object.values(savedRanks).filter(Number.isFinite), 0);
    newOnly.forEach((id) => {
        seq += 1;
        firstSeen[id] = seq;
    });

    const curBatch = isObj(ui.latestBatch) ? ui.latestBatch : {};
    const batchIsNew = Object.keys(curBatch).some((id) => newOnly.includes(id));
    const latestBatch = batchIsNew ? curBatch : { ...(isObj(savedUi.latestBatch) ? savedUi.latestBatch : {}) };

    const out = {
        aiCopilotQuestions: mergeCards(s.aiCopilotQuestions, saved.followUps, (q) =>
            normalizeCopilotQuestion(q.question)
        ),
        copilotCodeQuestions: mergeCards(s.copilotCodeQuestions, saved.codeQuestions, (q) => q.id),
        copilotSkillQuestions: mergeCards(s.copilotSkillQuestions, saved.skillQuestions, (q) => q.id),
        copilotUi: {
            ...ui,
            cleared: { ...(isObj(savedUi.cleared) ? savedUi.cleared : {}), ...(isObj(ui.cleared) ? ui.cleared : {}) },
            unread,
            firstSeen,
            seq,
            latestBatch,
            // [2026-11-03] ADDED — INC-279 IncAssist: the Bookmarks tab's seen ids, saved and current together (like the
            // Proctoring tab's below). Absent on both sides → no key, exactly as before.
            ...(isObj(savedUi.bookmarksSeen) || isObj(ui.bookmarksSeen)
                ? {
                      bookmarksSeen: {
                          ...(isObj(savedUi.bookmarksSeen) ? savedUi.bookmarksSeen : {}),
                          ...(isObj(ui.bookmarksSeen) ? ui.bookmarksSeen : {}),
                      },
                  }
                : {}),
        },
    };
    if (isObj(saved.proctoringSeenIds) || isObj(s.copilotProctoringSeenIds)) {
        out.copilotProctoringSeenIds = {
            ...(isObj(saved.proctoringSeenIds) ? saved.proctoringSeenIds : {}),
            ...(isObj(s.copilotProctoringSeenIds) ? s.copilotProctoringSeenIds : {}),
        };
    }
    return out;
}
